<template>
	<div class="easy-donation-box" v-if="useFormBuilder && displayFields.length > 0">
		<div class="single-credit-cardd">
			<div class="wpcm-row wpmc-justify-content-center">
				<template v-for="field in displayFields" :key="field.id">
					<!-- Text Input Fields -->
					<div 
						v-if="['text', 'email', 'tel'].includes(field.type)"
						:class="getFieldColumnClass(field)"
					>
						<div class="textfield">
							<input
								:type="field.type"
								:id="field.name"
								:name="field.name"
								:placeholder="field.placeholder || field.label"
								:required="field.required"
								:disabled="store.loading"
								v-model="store.billing_fields[getStoreKey(field)]"
							/>
						</div>
					</div>

					<!-- Textarea Field -->
					<div 
						v-if="field.type === 'textarea'"
						class="wpcm-col-md-12"
					>
						<div class="textfield">
							<textarea
								:id="field.name"
								:name="field.name"
								:placeholder="field.placeholder || field.label"
								:required="field.required"
								:disabled="store.loading"
								rows="4"
								v-model="store.billing_fields[getStoreKey(field)]"
							></textarea>
						</div>
					</div>

					<!-- Select Field -->
					<div 
						v-if="field.type === 'select'"
						class="wpcm-col-md-12"
					>
						<div class="textfield">
							<el-select
								:id="field.name"
								:name="field.name"
								:placeholder="field.placeholder || 'Please select...'"
								:required="field.required"
								:disabled="store.loading"
								filterable
								:model-value="store.billing_fields[getStoreKey(field)]"
								@update:model-value="setStoreFieldValue(field, $event)"
								style="width: 100%"
							>
								<el-option
									v-for="option in field.options || []"
									:key="option.value"
									:label="option.label"
									:value="option.value"
								/>
							</el-select>
						</div>
					</div>

					<!-- Checkbox Field -->
					<div 
						v-if="field.type === 'checkbox'"
						class="wpcm-col-md-12"
					>
						<div class="textfield">
							<el-checkbox
								:id="field.name"
								:name="field.name"
								:required="field.required"
								:disabled="store.loading"
								v-model="store.billing_fields[getStoreKey(field)]"
							>
								{{ field.label }}
							</el-checkbox>
						</div>
					</div>

					<!-- Anonymous Donation Field -->
					<div 
						v-if="field.type === 'anonymous_donation'"
						class="wpcm-col-md-12 wpcm-anonymous-field"
					>
						<el-checkbox
							id="anonymous_donation"
							name="anonymous_donation"
							:disabled="store.loading"
							v-model="store.billing_fields.anonymous_donation"
						>
							{{ field.label || strings?.anonymous_donation || 'Make this donation anonymous' }}
						</el-checkbox>
					</div>
				</template>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed, onMounted } from 'vue';
import { useDonationFormsStore } from '../store';

const store = useDonationFormsStore();
const props = defineProps([
	"is_logged_in", "email", 'show_country', 'show_county', 'show_city', 
	'show_postal', 'show_tax', 'show_phone_no', 'show_company', 
	'show_anonymous_donation', 'enable_test_card_information', "strings",
	'use_form_builder', 'form_builder_config', 'formFields'
]);

const fieldNameMap: Record<string, string> = {
	'first_name': 'first_name',
	'last_name': 'last_name',
	'email': 'email',
	'phone': 'phone',
	'company': 'company',
	'address': 'address',
	'city': 'city',
	'state': 'state',
	'zip': 'zip',
	'country': 'country',
	'tax': 'tax',
	'anonymous_donation': 'anonymous_donation'
};

const useFormBuilder = computed(() => {
	return props.use_form_builder === true && (props.form_builder_config?.fields?.length > 0 || props.formFields?.length > 0);
});

const computedFormFields = computed(() => {
	if (!useFormBuilder.value) return [];
	return props.form_builder_config?.fields || [];
});

const displayFields = computed(() => {
	if (props.formFields && Array.isArray(props.formFields) && props.formFields.length > 0) {
		return props.formFields;
	}
	if (computedFormFields.value && Array.isArray(computedFormFields.value) && computedFormFields.value.length > 0) {
		return computedFormFields.value;
	}
	return [];
});

const getStoreKey = (field: any): string => {
	return fieldNameMap[field.name] || field.name;
};

const setStoreFieldValue = (field: any, value: any) => {
	store.billing_fields[getStoreKey(field)] = value;
};

const getFieldColumnClass = (field: any) => {
	if (field.name === 'first_name' || field.name === 'last_name') {
		return 'wpcm-col-md-6';
	}
	return 'wpcm-col-md-12';
};

const initializeFields = () => {
	const fields = displayFields.value;
	if (!fields || !fields.length) return;

	for (const field of fields) {
		const key = getStoreKey(field);
		if (!(key in store.billing_fields)) {
			if (field.type === 'checkbox' || field.type === 'anonymous_donation') {
				store.billing_fields[key] = false;
			} else {
				store.billing_fields[key] = '';
			}
		}
	}

	if (props.is_logged_in && props.email) {
		store.billing_fields.email = props.email;
	}
};

onMounted(() => {
	initializeFields();
});
</script>

<style scoped>
/* Styles are inherited from the parent BillingInfo component */
.easy-donation-box {
	width: 100%;
}

.textfield {
	margin-bottom: 15px;
}

.textfield input,
.textfield textarea {
	width: 100%;
	padding: 12px;
	border: 1px solid #ddd;
	border-radius: 4px;
	font-size: 14px;
	transition: border-color 0.3s ease;
}

.textfield input:focus,
.textfield textarea:focus {
	outline: none;
	border-color: #409eff;
	box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.1);
}

.textfield textarea {
	resize: vertical;
	min-height: 80px;
}

.wpcm-anonymous-field {
	margin-top: 10px;
}
</style>